import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Gift, Truck, Sparkles } from "lucide-react";

const empty = {
  label: "",
  threshold: 1499,
  kind: "shipping",
  gift_product_ids: [],
  discount_percent: null,
  emoji: "🎁",
  display_order: 0,
  is_active: true,
};

const kindIcon = { shipping: Truck, gift: Gift, discount: Sparkles };

const AdminRewards = () => {
  const [tiers, setTiers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/admin/rewards").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...editing, threshold: Number(editing.threshold), display_order: Number(editing.display_order || 0) };
      if (editing.id) await api.patch(`/admin/rewards/${editing.id}`, body);
      else await api.post("/admin/rewards", body);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.label}"?`)) return;
    await api.delete(`/admin/rewards/${t.id}`);
    toast.success("Deleted"); load();
  };

  const toggleGift = (pid) => {
    const ids = editing.gift_product_ids || [];
    setEditing({
      ...editing,
      gift_product_ids: ids.includes(pid) ? ids.filter((x) => x !== pid) : [...ids, pid],
    });
  };

  return (
    <div data-testid="admin-rewards-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1">Conversion</p>
          <h1 className="font-hero text-3xl">Rewards & unlocks</h1>
          <p className="text-sm text-charcoal/70 max-w-xl mt-1">
            Tiered rewards shown on the cart drawer progress bar. The customer unlocks each as their subtotal grows.
          </p>
        </div>
        <button className="btn-primary" onClick={() => { setEditing({ ...empty }); setOpen(true); }} data-testid="admin-new-reward">
          <Plus size={14} className="mr-2" /> New tier
        </button>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {tiers.map((t) => {
          const Icon = kindIcon[t.kind] || Gift;
          return (
            <div key={t.id} className="bg-white rounded-2xl border border-border p-5" data-testid={`reward-tier-${t.kind}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center text-xl">{t.emoji || "🎁"}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditing({ ...t }); setOpen(true); }} className="text-charcoal/70 hover:text-charcoal" aria-label="edit"><Pencil size={14} /></button>
                  <button onClick={() => remove(t)} className="text-charcoal/70 hover:text-rose" aria-label="delete"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="font-cormorant text-xl leading-tight">{t.label}</p>
              <p className="font-label text-[10px] uppercase tracking-[0.2em] text-charcoal/70 mt-1 flex items-center gap-1">
                <Icon size={11} /> {t.kind}
              </p>
              <p className="mt-4 font-hero text-2xl text-charcoal">{formatINR(t.threshold)}</p>
              <p className="text-xs text-charcoal/70">Unlocked when subtotal reaches</p>
              {t.kind === "discount" && t.discount_percent && (
                <p className="mt-2 text-sm text-rose">Gives {t.discount_percent}% off</p>
              )}
              {t.kind === "gift" && (
                <p className="mt-2 text-sm text-rose">{(t.gift_products || []).length} gift option{(t.gift_products || []).length !== 1 ? "s" : ""}</p>
              )}
              {!t.is_active && <p className="mt-2 text-xs uppercase text-charcoal/70 tracking-[0.2em]">Paused</p>}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? null : setOpen(false))}>
        <DialogContent className="bg-cream max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-hero text-2xl">{editing?.id ? "Edit" : "New"} reward tier</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] gap-3">
                <div>
                  <label className="eyebrow">Icon</label>
                  <Input value={editing.emoji || ""} onChange={(e) => setEditing({ ...editing, emoji: e.target.value })} placeholder="🎁" />
                </div>
                <div>
                  <label className="eyebrow">Label (shown to customer)</label>
                  <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} data-testid="reward-label-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow">Threshold (INR)</label>
                  <Input type="number" value={editing.threshold} onChange={(e) => setEditing({ ...editing, threshold: e.target.value })} />
                </div>
                <div>
                  <label className="eyebrow">Kind</label>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={editing.kind}
                    onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                  >
                    <option value="shipping">Free shipping</option>
                    <option value="gift">Free gift (picker)</option>
                    <option value="discount">Percent off</option>
                  </select>
                </div>
              </div>

              {editing.kind === "discount" && (
                <div>
                  <label className="eyebrow">Discount percent</label>
                  <Input
                    type="number" value={editing.discount_percent || ""}
                    onChange={(e) => setEditing({ ...editing, discount_percent: Number(e.target.value) })}
                    placeholder="10"
                  />
                </div>
              )}

              {editing.kind === "gift" && (
                <div>
                  <label className="eyebrow block mb-2">Gift product options (pick 2–4)</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[280px] overflow-y-auto p-1">
                    {products.map((p) => {
                      const selected = (editing.gift_product_ids || []).includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => toggleGift(p.id)}
                          className={`text-left rounded-xl overflow-hidden border-2 transition ${selected ? "border-charcoal" : "border-border/50 hover:border-charcoal/40"}`}
                        >
                          <div className="aspect-[4/5] bg-blush/30">
                            {(p.images || [])[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                          </div>
                          <div className="p-1.5">
                            <p className="font-cormorant text-xs leading-tight truncate">{p.name}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow">Display order</label>
                  <Input type="number" value={editing.display_order || 0} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} />
                </div>
                <label className="flex items-center gap-2 font-label text-[12px] uppercase tracking-[0.2em] self-end pb-2">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-rose" />
                  Active
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button className="btn-outline" onClick={() => setOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={save} data-testid="admin-save-reward">Save tier</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRewards;
